import { useState, useId, type FormEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Copy, Check, Plus, Trash2 } from 'lucide-react'
import type { RootState, AppDispatch } from '../store'
import type { GeneratedApiToken } from '../data/accountTypes'
import { API_TOKEN_NAME_MAX_LENGTH, ApiTokenRequestErrorSchema } from '../schemas/accountSchemas'
import { login } from './UserMenu'
import { useDeleteApiTokenMutation, useGenerateApiTokenMutation, useGetApiTokensQuery } from '../services/projectDataApi'
import '../pages/Settings.css'

function requestError(error: unknown, fallback: string): string {
  const parsed = ApiTokenRequestErrorSchema.safeParse(error)
  return parsed.success ? parsed.data.data.error : fallback
}

/** Shared by settings and the coding-agent guide; reset one-time secrets when the user changes. */
export default function ApiTokenControls() {
  const { user } = useSelector((s: RootState) => s.auth)
  return <UserApiTokenControls key={user?.id ?? 'signed-out'} user={user} />
}

function UserApiTokenControls({ user }: { user: RootState['auth']['user'] }) {
  const dispatch = useDispatch<AppDispatch>()
  const nameInputId = useId()
  const tokenQuery = useGetApiTokensQuery(user?.id ?? '', { skip: !user })
  const [generateApiToken, generateState] = useGenerateApiTokenMutation()
  const [deleteApiToken, deleteState] = useDeleteApiTokenMutation()
  const tokens = tokenQuery.currentData?.tokens ?? []
  const [name, setName] = useState('')
  const [newToken, setNewToken] = useState<GeneratedApiToken | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleGenerate(event: FormEvent) {
    event.preventDefault()
    if (!user) {
      login(dispatch)
      return
    }
    if (!name.trim() || generateState.isLoading || newToken) return
    setError(null)
    try {
      const pending = generateApiToken({ userId: user.id, name: name.trim() })
      const result = await pending.unwrap()
      setNewToken(result)
      // The secret is displayed only in this component, not retained in the mutation cache.
      pending.reset()
      setName('')
      setCopied(false)
      setConfirmDeleteId(null)
    } catch (error) {
      setError(requestError(error, 'Unable to create a token. Please try again.'))
    }
  }

  async function handleCopy() {
    if (!newToken) return
    try {
      await navigator.clipboard.writeText(newToken.token)
      setCopied(true)
      setError(null)
    } catch {
      setError('Unable to copy automatically. Select and copy the token above.')
    }
  }

  async function handleDelete(id: string) {
    if (!user || deleteState.isLoading) return
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id)
      return
    }
    setError(null)
    try {
      await deleteApiToken({ userId: user.id, id }).unwrap()
      setConfirmDeleteId(null)
      if (newToken?.id === id) setNewToken(null)
    } catch (error) {
      setError(requestError(error, 'Unable to revoke the token. Please try again.'))
    }
  }

  return (
    <>
      {newToken && (
        <div className="settings-token__reveal" role="status">
          <p className="settings-token__reveal-label">
            Copy the token{newToken.name ? ` for “${newToken.name}”` : ''} now. It won&apos;t be shown again.
          </p>
          <div className="settings-token__reveal-row">
            <code className="settings-token__value">{newToken.token}</code>
            <button
              type="button"
              className="settings-token__copy-btn"
              onClick={handleCopy}
              aria-label={copied ? 'Copied' : 'Copy token'}
              data-analytics-event="settings-token-copy"
            >
              {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            className="settings-token__dismiss-btn"
            onClick={() => setNewToken(null)}
            data-analytics-event="settings-token-dismiss-button"
          >
            I&apos;ve copied the token
          </button>
        </div>
      )}

      {tokenQuery.isLoading ? (
        <div className="settings-token__status" role="status">Loading tokens...</div>
      ) : tokenQuery.isError ? (
        <div className="settings-token__error" role="alert">
          Unable to load your tokens.{' '}
          <button type="button" className="settings-token__copy-btn" onClick={() => void tokenQuery.refetch()}>
            Retry
          </button>
        </div>
      ) : user && tokens.length === 0 ? (
        <p className="settings-token__none">No API tokens yet.</p>
      ) : null}

      {tokens.length > 0 && (
        <ul className="settings-token__list" aria-label="API tokens">
          {tokens.map((token) => (
            <li className="settings-token__item" key={token.id}>
              <div className="settings-token__info">
                <strong className="settings-token__name">{token.name}</strong>
                <span className="settings-token__exists">
                  Created {new Date(token.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="settings-token__actions">
                {confirmDeleteId === token.id && (
                  <>
                    <span className="settings-token__warning">Apps using this token will lose access.</span>
                    <button
                      type="button"
                      className="settings-token__copy-btn"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={deleteState.isLoading}
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={`settings-token__delete-btn ${confirmDeleteId === token.id ? 'settings-token__delete-btn--confirm' : ''}`}
                  onClick={() => void handleDelete(token.id)}
                  disabled={deleteState.isLoading}
                  aria-label={`${confirmDeleteId === token.id ? 'Confirm revoke' : 'Revoke'} ${token.name}`}
                  data-analytics-event="settings-token-revoke-button"
                >
                  <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                  {deleteState.isLoading && confirmDeleteId === token.id ? 'Revoking...' : confirmDeleteId === token.id ? 'Confirm revoke' : 'Revoke'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="settings-token__error" role="alert">{error}</p>}

      {!newToken && (
        <form className="settings-token__create" onSubmit={handleGenerate}>
          <label htmlFor={nameInputId}>Token name</label>
          <div className="settings-token__create-row">
            <input
              id={nameInputId}
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Work laptop or CI"
              maxLength={API_TOKEN_NAME_MAX_LENGTH}
              required
              disabled={generateState.isLoading}
              autoComplete="off"
            />
            <button
              type="submit"
              className="settings-token__generate-btn"
              disabled={generateState.isLoading || !name.trim()}
              data-analytics-event="settings-token-generate-button"
            >
              <Plus size={16} strokeWidth={1.75} aria-hidden />
              {generateState.isLoading ? 'Creating...' : 'Create token'}
            </button>
          </div>
          <p className="settings-token__hint">Creating a token keeps your existing tokens active.</p>
        </form>
      )}
    </>
  )
}
